// FrameInternalColorsTab.js - Frame internal colors configuration
import {
  React,
  useEffect,
  useState,
  Box,
  Button,
  CircularProgress,
  Stack,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC,
  FrameColorDialog,
  InfoBox
} from './FinishCommon';
import { useProductScope } from '../utils/productScope';

const FrameInternalColorsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const { canSeeScope } = useProductScope();

  const fetchData = () => {
    setLoading(true);
    authFetch('/finish/internal-colors')
      .then(data => { setRows(data); setLoading(false); })
      .catch(err => { console.error('Failed to fetch:', err); setLoading(false); });
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (formData) => {
    try {
      let updatedRow;
      if (editData?.id) {
        updatedRow = await authFetchRequest(`/finish/internal-colors/${editData.id}`, formData, 'PUT');
        setRows(prevRows => prevRows.map(row => row.id === editData.id ? updatedRow : row));
        if (selectedRow?.id === editData.id) setSelectedRow(updatedRow);
      } else {
        updatedRow = await authFetchRequest('/finish/internal-colors', formData, 'POST');
        setRows(prevRows => [...prevRows, updatedRow]);
      }
      setOpenDialog(false);
      setEditData(null);
    } catch (err) {
      console.error('Save failed:', err);
      alertC('Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const confirmed = await confirmC('Are you sure you want to delete this internal color?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/finish/internal-colors/${selectedRow.id}`, null, 'DELETE');
      setRows(prevRows => prevRows.filter(row => row.id !== selectedRow.id));
      setSelectedRow(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'label', headerName: 'Label', width: 200 },
    { field: 'value', headerName: 'Color Code', width: 120 },
    { field: 'colorPreview', headerName: 'Preview', width: 80,
      renderCell: (params) => (
        <Box sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: params.row.value,
          border: '1px solid #ccc'
        }} />
      )
    },
    { field: 'applies_to', headerName: 'Applies To', width: 120 },
    { field: 'active', headerName: 'Active', width: 100, renderCell: (params) => params.value ? 'Yes' : 'No' },
    { field: 'sort_order', headerName: 'Sort Order', width: 100 }
  ];

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => { setEditData(null); setOpenDialog(true); }}>
          Add Internal Color
        </Button>
        <Button variant="contained" color="secondary" onClick={() => { setEditData(selectedRow); setOpenDialog(true); }} disabled={!selectedRow}>
          Edit Color
        </Button>
        <Button variant="outlined" color="error" onClick={handleDelete} disabled={!selectedRow}>
          Delete Color
        </Button>
      </Stack>

      <InfoBox />

      {loading ? <CircularProgress /> : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={rows.filter((r) => canSeeScope(r.applies_to))}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => setSelectedRow(params.row)}
        />
      )}

      <FrameColorDialog
        open={openDialog}
        data={editData}
        title="Internal Color"
        onClose={() => { setOpenDialog(false); setEditData(null); }}
        onSave={handleSave}
      />
    </Box>
  );
};

export default FrameInternalColorsTab;
